import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ContentPipe } from 'src/app/services/content/content.pipe';
import { ContentPipeModule } from 'src/app/services/content/content-pipe.module';
import { DropdownOption } from 'src/app/shared/mine-dropdown/mine-dropdown.interface';
import { MenuPositionEnum } from 'src/app/shared/mine-action-menu/mine-action-menu-enum';
import { NotificationInboxDto, NotificationTypeEnum } from '../../models/notification.interface';
import { MineServiceIconComponent } from 'src/app/shared/mine-service-icon/mine-service-icon.component';
import { SystemIdToIconPipe } from 'src/app/shared/pipes/system-id-to-icon.pipe';
import { NotificationActionMenuComponent } from '../../notification-action-menu/notification-action-menu.component';

const SYSTEM_LOGO_NOTIFICATION_TYPES: ReadonlySet<NotificationTypeEnum> = new Set([
    NotificationTypeEnum.SystemAssignOwner,
    NotificationTypeEnum.SystemApproved,
]);

@Component({
    standalone: true,
    selector: 'notification-item',
    templateUrl: './notification-item.component.html',
    styleUrls: ['./notification-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ContentPipeModule,
        NotificationActionMenuComponent,
        MineServiceIconComponent,
        SystemIdToIconPipe,
    ],
})
export class NotificationItemComponent {

    @Input() notification: NotificationInboxDto;

    @Output() itemClicked = new EventEmitter<NotificationInboxDto>();
    @Output() markRead = new EventEmitter<string>();
    @Output() markUnread = new EventEmitter<string>();
    @Output() deleteItem = new EventEmitter<string>();

    readonly isMenuOpen = signal(false);
    readonly menuPosition = MenuPositionEnum.Bottom;

    constructor(private contentPipe: ContentPipe) {}

    get showTitle(): boolean {
        return this.notification?.notificationType === NotificationTypeEnum.AssessmentNoteAdded && !!this.notification?.title;
    }

    get showSystemLogo(): boolean {
        const n = this.notification;
        return (
            !!this.systemId &&
            SYSTEM_LOGO_NOTIFICATION_TYPES.has(n.notificationType)
        );
    }

    get systemId(): string | undefined {
        return this.notification?.metadata?.systemId ?? this.notification?.entityId;
    }

    get iconId(): string {
        switch (this.notification?.notificationType) {
            case NotificationTypeEnum.AssessmentNoteAdded:
                return 'icon-comments';
            case NotificationTypeEnum.AutofillSuggestionsReady:
                // Mira-generated suggestions (Vendor Scout, Assessment Autofill, etc.) —
                // persistent-suggestions UI is the default now, so always show the Mira icon.
                return 'icon-mira';
            case NotificationTypeEnum.AiTemplateReady:
            case NotificationTypeEnum.AiTemplateFailed:
                // "Create a template with AI" is a Mira flow — show the Mira glyph like autofill.
                return 'icon-mira';
            default:
                return 'icon-assessment';
        }
    }

    get contextMenuOptions(): DropdownOption[] {
        return [
					{
						id: this.notification?.isRead ? 'markUnread' : 'markRead',
						value: this.notification?.isRead
							? this.contentPipe.transform('in-app-notifications.markAsUnread')
							: this.contentPipe.transform('in-app-notifications.markAsRead'),
						icon: 'mark-unread',
						iconColor: 'var(--mine-grey-24)',
					},
					{
						id: 'delete',
						value: this.contentPipe.transform('in-app-notifications.deleteNotification'),
						icon: 'trash-flat',
						iconColor: 'var(--mine-grey-24)',
					},
				];
    }

    onItemClick(): void {
        this.itemClicked.emit(this.notification);
    }

    onContextMenuAction(actionId: string): void {
        if (actionId === 'markRead') {
            this.markRead.emit(this.notification.id);
        } else if (actionId === 'markUnread') {
            this.markUnread.emit(this.notification.id);
        } else if (actionId === 'delete') {
            this.deleteItem.emit(this.notification.id);
        }
    }
}
